import { useState } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { STATUS_FAILURE, STATUS_INIT, STATUS_SUCCESS } from '../../common/constants/formikStatus.constant';
import { consentChoiceValue } from '../../common/constants/privacyPolicyPopup.constant';
import { HUBSPOT_COOKIE } from '../../constants/regex';
import { dataLayerSubscribe } from '../../utils/dataLayerEvents';

const HUBSPOT_PRODUCTION_URL =
  'https://api.hsforms.com/submissions/v3/integration/submit/6286664/500ac99c-d68b-4428-8e8c-622eafc5325c';
const HUBSPOT_STAGING_URL =
  'https://api.hsforms.com/submissions/v3/integration/submit/6286664/5afa3980-4d2d-4ab5-803f-12c3cf18365e';

export const SUCCESS_MESSAGE = 'Thanks for submitting the form.';
export const FAILURE_MESSAGE = 'Message failed to submit. Please try again.';

export interface NewsletterSignupValues {
  email: string;
  consent: number;
}

interface HubSpotSubmission {
  fields: { name: string; value: string | boolean }[];
  context: {
    pageName: string;
    pageUri: string;
    hutk?: string;
  };
}

function getSubmitUrl() {
  const isLocalOrStaging =
    typeof window === 'undefined' ||
    window.location.href.includes('localhost:') ||
    window.location.href.includes('staging');

  return isLocalOrStaging ? HUBSPOT_STAGING_URL : HUBSPOT_PRODUCTION_URL;
}

/**
 * Owns the newsletter signup submit flow shared by every newsletter form on the site:
 * Formik state, the privacy-policy consent gate, the HubSpot Forms v3 POST, and the
 * success/failure popup state.
 */
export function useNewsletterSignup() {
  const [openPrivacyPolicyPopup, setOpenPrivacyPolicyPopup] = useState(false);
  const [openLastStepPopup, setOpenLastStepPopup] = useState(false);
  const [message, setMessage] = useState('');

  const formik = useFormik<NewsletterSignupValues>({
    initialStatus: STATUS_INIT,
    initialValues: {
      email: '',
      consent: consentChoiceValue.doNotConsent,
    },
    validationSchema: Yup.object().shape({
      email: Yup.string().email('Invalid email address').required('Email is required'),
    }),
    onSubmit: (values) => {
      if (!values.consent) {
        setOpenPrivacyPolicyPopup(true);
        return;
      }

      setOpenPrivacyPolicyPopup(false);

      const data: HubSpotSubmission = {
        fields: [
          {
            name: 'email',
            value: values.email,
          },
          {
            name: 'gita_newsletter_subscriber',
            value: true,
          },
          {
            name: 'lifecyclestage',
            value: 'subscriber',
          },
        ],
        context: {
          pageName: document.title ?? '',
          pageUri: window.location?.href ?? '',
        },
      };

      // Add hubspot tracking cookie if available
      const hubspotTrackingCookie = document.cookie.replace(HUBSPOT_COOKIE, '$1');
      if (hubspotTrackingCookie !== '') data.context.hutk = hubspotTrackingCookie;

      fetch(getSubmitUrl(), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      })
        .then((response) => {
          // fetch only rejects on network errors, so a 4xx/5xx from HubSpot lands here rather
          // than in .catch() — everything below has to gate on response.ok explicitly.
          formik.resetForm();
          formik.setStatus(response.ok ? STATUS_SUCCESS : STATUS_FAILURE);
          setMessage(response.ok ? SUCCESS_MESSAGE : FAILURE_MESSAGE);
          if (response.ok) dataLayerSubscribe();
        })
        .catch(() => {
          formik.setStatus(STATUS_FAILURE);
          setMessage(FAILURE_MESSAGE);
        })
        .finally(() => {
          formik.setSubmitting(false);
          setOpenLastStepPopup(true);
        });
    },
  });

  const onChoosingConsent = (choice: number) => {
    if (choice === consentChoiceValue.consent) {
      formik.values.consent = choice;
      formik.handleSubmit();
    } else {
      setOpenPrivacyPolicyPopup(false);
      formik.setSubmitting(false);
    }
  };

  const onCloseLastStepPopup = () => {
    setOpenLastStepPopup(false);
  };

  return {
    formik,
    openPrivacyPolicyPopup,
    openLastStepPopup,
    message,
    onChoosingConsent,
    onCloseLastStepPopup,
  };
}
